import type { StreamingBlock } from "@storytell/streaming";
import { getBlockContent } from "@storytell/streaming";
import { onButtonKeyDown } from "@storytell/ui/buttons";
import { TbBrandLinkedin, TbCheck, TbChevronRight, TbCopy, TbMail, TbUser } from "solid-icons/tb";
import { createMemo, createSignal, For, Show } from "solid-js";
import { safeParseBlockJson } from "../message/parseBlockContent";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { ProcessingSpinner } from "../ui";
import { useCopy } from "../ui/useCopy";
import { collapsibleOpenHint } from "../util/collapsibleOpenHint";

export interface ScoutData {
  type: "scout";
  searchType: string;
  description?: string;
  domain?: string;
  companyName?: string;
  personName?: string;
  categories?: string[];
  linkedInUrl?: string;
  emails?: ScoutEmailResult[];
  validEmailCount: number;
  creditsUsed: number;
  duration?: string;
}

export interface ScoutEmailResult {
  email: string;
  emailStatus: string;
  validEmail: boolean;
  personFullName?: string;
  personJobTitle?: string;
  personLinkedInUrl?: string;
  personCompanyName?: string;
}

interface ScoutRendererProps {
  block: StreamingBlock;
}

interface ExpandableScoutResultItemProps {
  result: ScoutEmailResult;
  index: number;
}

const getStatusBadgeClasses = (status: string): string => {
  switch (status.toLowerCase()) {
    case "valid":
      return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400";
    case "risky":
      return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400";
    case "blacklisted":
      return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
    default:
      return "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400";
  }
};

const getStatusLabel = (status: string, t: (key: string) => string): string => {
  switch (status.toLowerCase()) {
    case "valid":
      return t("scout_status_valid");
    case "risky":
      return t("scout_status_risky");
    case "blacklisted":
      return t("scout_status_blacklisted");
    case "not_found":
      return t("scout_status_not_found");
    default:
      return status;
  }
};

export const ExpandableScoutResultItem = (props: ExpandableScoutResultItemProps) => {
  const rt = useChatRuntime();
  const [isExpanded, setIsExpanded] = createSignal(false);
  const { copied, copy } = useCopy({ timeout: 1500, write: rt.actions.copyText });

  const toggle = () => setIsExpanded(!isExpanded());

  const handleCopyEmail = async (e: MouseEvent) => {
    e.stopPropagation();
    await copy(props.result.email);
  };

  const hasPersonDetails = () =>
    !!(props.result.personFullName || props.result.personJobTitle || props.result.personCompanyName);

  return (
    <div class="border-b border-gray-200 last:border-b-0 dark:border-slate-700">
      <div
        role="button"
        tabIndex={hasPersonDetails() ? 0 : -1}
        aria-disabled={!hasPersonDetails()}
        aria-expanded={hasPersonDetails() ? isExpanded() : undefined}
        onClick={() => hasPersonDetails() && toggle()}
        onKeyDown={onButtonKeyDown(() => hasPersonDetails() && toggle())}
        class="group/scout-result-item flex w-full items-center gap-2 px-3 py-2.5 hover:bg-canvas-hover"
        classList={{ "cursor-pointer": hasPersonDetails() }}
      >
        <Show when={hasPersonDetails()}>
          <TbChevronRight
            size={14}
            class="shrink-0 text-muted-text transition-transform duration-100"
            classList={{ "rotate-90": isExpanded() }}
          />
        </Show>
        <div class="flex size-5 shrink-0 items-center justify-center rounded bg-indigo-100 dark:bg-indigo-900/30">
          <TbMail class="size-3 text-indigo-600 dark:text-indigo-400" />
        </div>
        <span class="flex-1 truncate text-left text-sm text-primary-text">{props.result.email}</span>
        <Show when={isExpanded() && props.result.personFullName}>
          <span class="max-w-30 truncate text-xs text-secondary-text">{props.result.personFullName}</span>
        </Show>
        <span class={`shrink-0 rounded px-1.5 py-0.5 text-xs ${getStatusBadgeClasses(props.result.emailStatus)}`}>
          {getStatusLabel(props.result.emailStatus, rt.t)}
        </span>
        <Show when={hasPersonDetails() && !isExpanded()}>
          <span class={collapsibleOpenHint.scoutResultItem} aria-hidden>
            {rt.t("common.open")}
          </span>
        </Show>
        <Show when={!hasPersonDetails()} fallback={null}>
          <button
            type="button"
            onClick={(e) => void handleCopyEmail(e)}
            class="rounded p-1 transition-colors hover:bg-gray-200 dark:hover:bg-gray-700"
            title={rt.t("scout_copy_email")}
          >
            <Show when={copied()} fallback={<TbCopy class="size-3.5 text-secondary-text" />}>
              <TbCheck class="size-3.5 text-green-600 dark:text-green-400" />
            </Show>
          </button>
        </Show>
      </div>

      <Show when={isExpanded()}>
        <div class="px-3 pt-1 pb-3">
          <div class="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/30">
            <div class="mb-2 flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="text-sm font-medium text-primary-text">{props.result.email}</span>
                <button
                  type="button"
                  onClick={(e) => void handleCopyEmail(e)}
                  class="rounded p-1 transition-colors hover:bg-gray-200 dark:hover:bg-gray-700"
                  title={rt.t("scout_copy_email")}
                >
                  <Show when={copied()} fallback={<TbCopy class="size-3.5 text-secondary-text" />}>
                    <TbCheck class="size-3.5 text-green-600 dark:text-green-400" />
                  </Show>
                </button>
              </div>
              <span class="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                #{props.index + 1}
              </span>
            </div>

            <Show when={hasPersonDetails()}>
              <div class="mt-3 space-y-1.5 border-t border-gray-200 pt-3 dark:border-gray-700">
                <Show when={props.result.personFullName}>
                  <div class="flex items-center gap-2 text-sm">
                    <TbUser class="size-4 shrink-0 text-secondary-text" />
                    <span class="text-primary-text">{props.result.personFullName}</span>
                  </div>
                </Show>

                <Show when={props.result.personJobTitle}>
                  <div class="flex items-center gap-2 text-sm">
                    <span class="w-4 shrink-0" />
                    <span class="text-secondary-text">{props.result.personJobTitle}</span>
                  </div>
                </Show>

                <Show when={props.result.personCompanyName}>
                  <div class="flex items-center gap-2 text-sm">
                    <span class="w-4 shrink-0" />
                    <span class="text-secondary-text">{props.result.personCompanyName}</span>
                  </div>
                </Show>

                <Show when={props.result.personLinkedInUrl}>
                  <a
                    href={props.result.personLinkedInUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="mt-2 flex items-center gap-2 text-sm text-cyan-600 hover:underline dark:text-cyan-400"
                  >
                    <TbBrandLinkedin class="size-4 shrink-0" />
                    <span>{rt.t("scout_view_linkedin")}</span>
                  </a>
                </Show>
              </div>
            </Show>
          </div>
        </div>
      </Show>
    </div>
  );
};

export function ScoutRenderer(props: ScoutRendererProps) {
  const rt = useChatRuntime();

  const scoutData = createMemo<ScoutData | null>(() => {
    if (props.block.finalContent?.type === "text" && props.block.finalContent.text) {
      const parsed = safeParseBlockJson(props.block.finalContent.text) as ScoutData | null;
      if (parsed?.type === "scout") return parsed;
    }
    const content = getBlockContent(props.block.blockId);
    if (content) {
      const parsed = safeParseBlockJson(content) as ScoutData | null;
      if (parsed?.type === "scout") return parsed;
    }
    return null;
  });

  const isStreaming = () => !props.block.isComplete;

  return (
    <Show
      when={scoutData()}
      fallback={
        <Show when={isStreaming()}>
          <div class="flex items-center gap-3 text-sm text-secondary-text">
            <ProcessingSpinner class="size-4" />
            <span>{rt.t("scout_finding_contacts")}</span>
          </div>
        </Show>
      }
    >
      {(data) => (
        <Show when={data().emails?.length}>
          <div class="overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700">
            <For each={data().emails}>
              {(result, index) => <ExpandableScoutResultItem result={result} index={index()} />}
            </For>
          </div>
        </Show>
      )}
    </Show>
  );
}
